import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Plus, Eye, Search, RefreshCw, Star, AlertCircle } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { adminGetProperties, adminDeleteProperty } from '../../services/api';
import { formatPrice } from '../../utils/constants';
import toast from 'react-hot-toast';
import './Admin.css';

const STATUS_STYLE = {
  UNDER_CONSTRUCTION: { bg:'rgba(245,158,11,0.1)', color:'#f59e0b' },
  READY_TO_MOVE:      { bg:'rgba(16,185,129,0.1)', color:'#10b981' },
  SOLD_OUT:           { bg:'rgba(107,114,128,0.1)', color:'#6b7280' },
};

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchProps = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { page, size: 20 };
      if (filterCity) params.city = filterCity;
      if (filterCat)  params.category = filterCat;
      const res = await adminGetProperties(params);
      const d = res.data.data;
      setProperties(d.content || []);
      setTotalPages(d.totalPages || 0);
      setTotal(d.totalElements || 0);
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expired.' : 'Failed to load properties. Is the backend running?');
    } finally { setLoading(false); }
  }, [page, filterCity, filterCat]);

  useEffect(() => { fetchProps(); }, [fetchProps]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis will permanently remove the property and all its images.`)) return;
    try {
      await adminDeleteProperty(id);
      toast.success('Property deleted');
      fetchProps();
    } catch { toast.error('Failed to delete. Please try again.'); }
  };

  const filtered = properties.filter(p =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.locality?.toLowerCase().includes(search.toLowerCase())
  );

  const ss = status => STATUS_STYLE[status] || STATUS_STYLE.UNDER_CONSTRUCTION;

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page__header">
          <div>
            <h1>Properties</h1>
            <p>{total} listing{total!==1?'s':''} in database</p>
          </div>
          <div style={{display:'flex',gap:'10px'}}>
            <button className="admin-btn-secondary" onClick={fetchProps}><RefreshCw size={14}/> Refresh</button>
            <Link to="/admin/properties/new" className="btn-primary"><Plus size={14}/> Add Property</Link>
          </div>
        </div>

        {error && (
          <div style={{background:'#fff0f0',border:'1px solid rgba(234,46,43,0.2)',padding:'12px 16px',borderRadius:'var(--radius)',color:'var(--primary)',fontSize:'13px',display:'flex',alignItems:'center',gap:'8px',marginBottom:'16px'}}>
            <AlertCircle size={15}/> {error}
          </div>
        )}

        <div className="admin-card">
          <div className="filter-bar">
            <div className="filter-input-wrap">
              <Search size={14}/>
              <input placeholder="Search by title or locality..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <select value={filterCity} onChange={e=>{ setFilterCity(e.target.value); setPage(0); }}>
              <option value="">All Cities</option>
              <option>Delhi NCR</option><option>Dholera SIR</option><option>Ahmedabad</option>
            </select>
            <select value={filterCat} onChange={e=>{ setFilterCat(e.target.value); setPage(0); }}>
              <option value="">All Categories</option>
              <option value="PROJECT">Projects</option>
              <option value="RESALE">Resale</option>
            </select>
          </div>

          {loading ? (
            <div className="admin-loading"><div className="admin-spinner"/><p>Loading properties...</p></div>
          ) : filtered.length === 0 ? (
            <div className="admin-empty-state">
              <p>{search || filterCity || filterCat ? 'No properties match your filters.' : 'No properties yet. Add your first listing.'}</p>
              {!(search || filterCity || filterCat) && (
                <Link to="/admin/properties/new" className="btn-primary" style={{marginTop:'8px'}}><Plus size={14}/> Add First Property</Link>
              )}
              {(search || filterCity || filterCat) && (
                <button className="link-btn" onClick={()=>{setSearch('');setFilterCity('');setFilterCat('');}}>Clear filters</button>
              )}
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr><th>Property</th><th>City</th><th>Type</th><th>Price</th><th>Status</th><th>Views</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td style={{minWidth:'200px'}}>
                      <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
                        {p.featured && <Star size={12} fill="var(--primary)" color="var(--primary)" style={{marginTop:'3px',flexShrink:0}}/>}
                        <div>
                          <strong style={{display:'block',fontSize:'14px',lineHeight:1.3}}>{p.title}</strong>
                          <span style={{fontSize:'12px',color:'var(--text-3)'}}>{p.locality} • {p.propertyCategory}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{fontSize:'13px'}}>{p.city}</td>
                    <td style={{fontSize:'13px',color:'var(--text-2)'}}>{p.propertyType || '—'}</td>
                    <td style={{fontWeight:700,color:'var(--primary)',fontSize:'13px',whiteSpace:'nowrap'}}>{formatPrice(p.price)}</td>
                    <td>
                      <span className="status-badge" style={{background:ss(p.status).bg,color:ss(p.status).color}}>
                        {p.status?.replace(/_/g,' ')}
                      </span>
                    </td>
                    <td style={{fontSize:'13px',color:'var(--text-3)'}}>{p.viewCount ?? 0}</td>
                    <td>
                      <div className="table-actions">
                        <a href={`/${p.propertyCategory==='PROJECT'?'projects':'properties'}/${p.slug}`} target="_blank" rel="noreferrer" className="btn-icon view" title="Preview"><Eye size={13}/></a>
                        <Link to={`/admin/properties/${p.id}/edit`} className="btn-icon edit" title="Edit"><Pencil size={13}/></Link>
                        <button className="btn-icon delete" onClick={()=>handleDelete(p.id,p.title)} title="Delete"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button disabled={page===0} onClick={()=>setPage(p=>p-1)} className="page-btn">← Prev</button>
              <span>Page {page+1} of {totalPages}</span>
              <button disabled={page>=totalPages-1} onClick={()=>setPage(p=>p+1)} className="page-btn">Next →</button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;
